import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { type TestRoundView } from '@apm/contracts';
import { type GanttMarker } from '../gantt/markers';
import { testingApi, testingKeys } from './api';

/**
 * Vòng test → vạch mốc trên Gantt — docs/plan-testing-tab.md §9.3 mức 2.
 *
 * Đây là câu trả lời cho "vẫn hiển thị được một phần trên kế hoạch": mở Gantt
 * là thấy tuần nào là tuần test, bấm vào vạch thì sang tab Kiểm thử **đúng
 * vòng ấy**.
 */

/** Ngày lịch của một mốc `DateTime` — Gantt làm việc theo ngày, không theo giờ. */
function calendarDay(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Phần thuần của việc đổi vòng thành mốc — tách ra để test được không cần DOM.
 *
 * Mỗi vòng cho **một** vạch ở ngày mở. Không vẽ thêm vạch ở ngày đóng: hai vạch
 * cho một vòng làm Gantt của dự án có năm vòng thành mười vạch, và vạch đóng
 * của vòng n gần như luôn trùng vạch mở của vòng n+1 — hai nhãn đè lên nhau ở
 * cùng một chỗ, nói cùng một điều.
 */
export function roundsToMarkers(
  rounds: readonly TestRoundView[],
  label: (round: TestRoundView) => string,
  title: (round: TestRoundView) => string,
  onSelect?: (roundId: string) => void,
): readonly GanttMarker[] {
  return rounds.map((round) => ({
    key: `test-round-${round.id}`,
    date: calendarDay(round.startedAt),
    label: label(round),
    title: title(round),
    ...(onSelect ? { onSelect: () => onSelect(round.id) } : {}),
  }));
}

/**
 * Mốc vòng test của một dự án, sẵn để đưa vào `GanttChart`.
 *
 * Không có `test:read` thì endpoint trả 403 và hook trả mảng rỗng — Gantt mất
 * vạch chứ không hiện thẻ lỗi, cùng cách `TaskBugLinks` xử lý. `retry: false`
 * để một người không có quyền không gõ cửa ba lần mỗi lần mở Gantt.
 */
export function useTestRoundMarkers(projectId: string): readonly GanttMarker[] {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const overview = useQuery({
    queryKey: testingKeys.overview(projectId, undefined),
    queryFn: () => testingApi.overview(projectId, undefined),
    retry: false,
  });

  return roundsToMarkers(
    overview.data?.rounds ?? [],
    (round) => t('gantt.roundMarker', { number: round.number }),
    (round) =>
      round.closedAt === null
        ? t('gantt.roundMarkerOpen', { number: round.number })
        : t('gantt.roundMarkerClosed', {
            number: round.number,
            date: calendarDay(round.closedAt),
          }),
    (roundId) => {
      /*
        Giữ nguyên phần còn lại của search: `projectId`, `asOf`, bộ lọc… Mất
        `projectId` là nhảy về dự án đầu danh sách, và người bấm tưởng mình vừa
        bấm sai chỗ.
      */
      void navigate({
        to: '/projects',
        /*
          `prev` khai kiểu rộng có chủ đích. `useNavigate()` gọi ngoài file
          route không biết mình đứng ở route nào, nên nó suy `prev` thành hợp
          của search **mọi** route — và phép spread khi ấy không biên dịch được
          (`taskView: 'list'` của route này không thuộc union kia). Dạng hàm là
          thứ giữ lại `projectId` và `asOf`; mất `projectId` là nhảy về dự án
          đầu danh sách, và người bấm tưởng mình bấm sai chỗ.
        */
        search: (prev: Record<string, unknown>) => ({ ...prev, tab: 'testing', roundId }),
      });
    },
  );
}
